'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { Check, AlertTriangle, Edit3 as EditIcon, Loader2 as Spinner } from 'lucide-react';
import { publicApi } from '@/lib/api';
import { TENANT_SLUG_NORMALIZER_REGEX } from '@/lib/platform-domain';

/**
 * Preview do slug do tenant + check de disponibilidade ao vivo.
 *
 * Comportamento:
 *   1. User digita "Nome da barbearia" → slug e gerado automaticamente
 *      via mesma normalizacao do backend (`generateSlugBase`).
 *   2. Botao "Editar" permite override manual (caso user queira slug
 *      diferente do nome).
 *   3. Debounce 400ms → GET /platform/check-slug?slug=... → mostra
 *      ✓ disponivel | ⚠ ocupado / reservado / invalido + sugestao.
 *
 * Reforca a arquitetura multi-tenant: user ve desde o cadastro o link
 * publico que vai compartilhar com clientes.
 */
interface SlugPreviewProps {
  barbershopName: string;
  rootDomain: string;
  /** Slug atual (controlled). Pai mantem state. */
  value: string;
  /** Notifica mudancas pro pai - pai usa pra submeter no form. */
  onChange: (slug: string) => void;
}

function normalizeSlug(input: string): string {
  return (
    input
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/\s+/g, '-')
      .replace(TENANT_SLUG_NORMALIZER_REGEX, '') || ''
  );
}

export function SlugPreview({
  barbershopName,
  rootDomain,
  value,
  onChange,
}: SlugPreviewProps) {
  const [editing, setEditing] = useState(false);

  const autoSlug = useMemo(
    () => normalizeSlug(barbershopName),
    [barbershopName],
  );

  // Mantem o slug sincronizado com o nome enquanto user nao tomar
  // controle manual via "Editar".
  useEffect(() => {
    if (!editing && value !== autoSlug) {
      onChange(autoSlug);
    }
  }, [autoSlug, editing, onChange, value]);

  // Check de disponibilidade com debounce + fetch direto (sem useQuery
  // do TanStack porque as paginas /login e /register nao tem
  // QueryClientProvider - usar useQuery aqui crasha o SSR com
  // "No QueryClient set" e Swarm rolla back o container).
  const [debounced, setDebounced] = useState(value);
  const [status, setStatus] = useState<{
    available: boolean;
    reason?: 'invalid' | 'reserved' | 'taken';
    suggestion?: string;
  } | null>(null);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), 400);
    return () => clearTimeout(t);
  }, [value]);

  useEffect(() => {
    if (debounced.length < 2) {
      setStatus(null);
      return;
    }
    let cancelled = false;
    setIsFetching(true);
    publicApi
      .checkSlugAvailability(debounced)
      .then((res) => {
        if (!cancelled) setStatus(res.data ?? null);
      })
      .catch(() => {
        if (!cancelled) setStatus(null);
      })
      .finally(() => {
        if (!cancelled) setIsFetching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced]);

  if (!barbershopName || value.length < 2) {
    return null;
  }

  const isAvailable = status?.available === true;
  const reason = status?.reason;
  const suggestion = status?.suggestion;

  return (
    <div className="slug-preview" aria-live="polite">
      <div className="slug-preview__row">
        <span className="slug-preview__label">Seu link será:</span>
        {editing ? (
          <span className="slug-preview__edit">
            <input
              type="text"
              value={value}
              onChange={(e) => onChange(normalizeSlug(e.target.value))}
              autoFocus
              maxLength={60}
              className="slug-preview__input"
              aria-label="Identificador (slug) da barbearia"
            />
            <span className="slug-preview__suffix">.{rootDomain}</span>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="slug-preview__action"
            >
              Pronto
            </button>
          </span>
        ) : (
          <span className="slug-preview__url">
            <strong>{value || '...'}</strong>.{rootDomain}
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="slug-preview__action"
              aria-label="Editar identificador"
            >
              <EditIcon size={12} aria-hidden="true" />
              Editar
            </button>
          </span>
        )}
      </div>

      <div
        className={`slug-preview__status slug-preview__status--${
          isFetching
            ? 'loading'
            : isAvailable
            ? 'ok'
            : reason ?? 'unknown'
        }`}
      >
        {isFetching ? (
          <>
            <Spinner size={12} className="animate-spin" aria-hidden="true" />
            Verificando disponibilidade…
          </>
        ) : isAvailable ? (
          <>
            <Check size={12} aria-hidden="true" />
            Disponível
          </>
        ) : reason === 'taken' ? (
          <>
            <AlertTriangle size={12} aria-hidden="true" />
            Esse identificador já está em uso.{' '}
            {suggestion && (
              <button
                type="button"
                onClick={() => {
                  onChange(suggestion);
                  setEditing(true);
                }}
                className="slug-preview__suggestion"
              >
                Usar <code>{suggestion}</code>
              </button>
            )}
          </>
        ) : reason === 'reserved' ? (
          <>
            <AlertTriangle size={12} aria-hidden="true" />
            Esse identificador é reservado da plataforma.{' '}
            {suggestion && (
              <button
                type="button"
                onClick={() => {
                  onChange(suggestion);
                  setEditing(true);
                }}
                className="slug-preview__suggestion"
              >
                Usar <code>{suggestion}</code>
              </button>
            )}
          </>
        ) : reason === 'invalid' ? (
          <>
            <AlertTriangle size={12} aria-hidden="true" />
            Use apenas letras minúsculas, números e hífen.
          </>
        ) : null}
      </div>
    </div>
  );
}
